import "./AuditView.scss";
import { useTranslation } from "react-i18next";
import { AuditType, UserAudit } from "../../model/domain/audit";
import {
  Alert,
  AlertType,
  Button,
  DateField,
  InputTextField,
  LoadingView,
  SelectorField,
} from "@cuatroochenta/co-admin-react-library";
import { translate } from "../../../I18n/i18n";
import { Col, Row, Table } from "react-bootstrap";
import { Center } from "../../model/domain/center";
import { useMemo } from "react";

interface AuditViewProps {
  centerId: number | null;
  type: AuditType;
  startDate: Date;
  endDate: Date;
  email: string;
  centers?: Center[];
  audits?: UserAudit[];
  initialLoading: boolean;
  loadingResults: boolean;
  error: string | null;
  onChangeCenterId: (centerId: number | null) => void;
  onChangeType: (type: AuditType) => void;
  onChangeStartDate: (startDate: string) => void;
  onChangeEndDate: (endDate: string) => void;
  onChangeEmail: (email: string) => void;
  onFilter: () => void;
}

export default function AuditView({
  centerId,
  type,
  startDate,
  endDate,
  email,
  centers,
  audits,
  initialLoading,
  loadingResults,
  error,
  onChangeCenterId,
  onChangeType,
  onChangeStartDate,
  onChangeEndDate,
  onChangeEmail,
  onFilter,
}: AuditViewProps) {
  const { t } = useTranslation();

  const centerSelectorOptions = useMemo(() => {
    return (
      centers?.map((center) => ({
        id: center.id,
        description: center.name,
      })) ?? []
    );
  }, [centers]);

  const typeSelectorOptions = useMemo(() => {
    return [
      {
        id: AuditType.LOGIN,
        description: translate(t, "audit.TR_TYPE_LOGIN"),
      },
      {
        id: AuditType.RESET_PASSWORD,
        description: translate(t, "audit.TR_TYPE_RESET_PASSWORD"),
      },
    ];
  }, [t]);

  if (initialLoading) {
    return <LoadingView loading />;
  }

  return (
    <>
      <Alert
        show={!!error}
        type={AlertType.DANGER}
        message={error || translate(t, "common.TR_ERROR_GENERIC")}
      />
      <Row className="audit-filters-row">
        <Col>
          <label htmlFor="centerId" className="mb-1">
            {translate(t, "common.TR_CENTER_LABEL")}
          </label>
          <SelectorField
            name="centerId"
            value={centerId?.toString() ?? ""}
            includeAllOption
            values={centerSelectorOptions}
            onChange={(value) => onChangeCenterId(value ? +value : null)}
          />
        </Col>

        <Col>
          <label htmlFor="type" className="mb-1">
            {translate(t, "audit.TR_FILTER_TYPE")}
          </label>
          <SelectorField
            name="type"
            value={type}
            values={typeSelectorOptions}
            onChange={(value) => onChangeType(value as AuditType)}
          />
        </Col>

        <Col sm={3}>
          <label htmlFor="email" className="mb-1">
            {translate(t, "audit.TR_FILTER_EMAIL")}
          </label>
          <InputTextField
            name="email"
            type="email"
            size={255}
            value={email}
            onChange={onChangeEmail}
            placeholder={translate(t, "audit.TR_FILTER_EMAIL_PLACEHOLDER")}
          />
        </Col>

        <Col className="d-flex flex-column">
          <label htmlFor="startDate" className="mb-1">
            {translate(t, "audit.TR_FILTER_DATE_FROM")}
          </label>
          <DateField
            name="startDate"
            type="DATE"
            value={startDate.toISOString()}
            onChange={onChangeStartDate}
            maxDate={endDate}
            readOnly
          />
        </Col>

        <Col className="d-flex flex-column">
          <label htmlFor="endDate" className="mb-1">
            {translate(t, "audit.TR_FILTER_DATE_TO")}
          </label>
          <DateField
            name="endDate"
            type="DATE"
            value={endDate.toISOString()}
            onChange={onChangeEndDate}
            minDate={startDate}
            readOnly
          />
        </Col>

        <Col xs="auto">
          <Button onClick={onFilter} content={translate(t, "audit.TR_APPLY")} />
        </Col>
      </Row>

      {loadingResults || audits ? (
        <AuditResultsView audits={audits} loadingResults={loadingResults} />
      ) : null}
    </>
  );
}

interface AuditResultsProps {
  audits: AuditViewProps["audits"];
  loadingResults: AuditViewProps["loadingResults"];
}

function AuditResultsView({ audits, loadingResults }: AuditResultsProps) {
  const { t } = useTranslation();

  if (loadingResults) {
    return <LoadingView loading />;
  }

  if (!audits || audits.length === 0) {
    return <p className="mt-3">{translate(t, "common.TR_NO_RESULTS_LABEL")}</p>;
  }

  return (
    <>
      <Table className="mt-3">
        <thead>
          <tr>
            <th>{translate(t, "audit.TR_COL_DATE")}</th>
            <th>{translate(t, "audit.TR_COL_EMAIL")}</th>
            <th>{translate(t, "audit.TR_COL_TYPE")}</th>
            <th>{translate(t, "audit.TR_COL_ERROR")}</th>
          </tr>
        </thead>
        <tbody>
          {audits.map((audit, index) => (
            <tr key={audit.id} className={index % 2 !== 0 ? "odd" : ""}>
              <td>{audit.createdAt}</td>
              <td>{audit.email}</td>
              <td>
                {translate(t, `audit.TR_TYPE_${audit.type.toUpperCase()}`)}
              </td>
              <td>{audit.error}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}
